import React, { useState, useEffect } from "react";
import { detectContext } from "./contextRegistry";

// Import all context plugins - they register themselves when imported
import "./contexts";

// LocalStorage key for persisting panel state
const STORAGE_KEY_MINIMIZED = "adminPanelMinimized";

/**
 * Load panel state from localStorage
 * Defaults to minimized (closed) on first visit
 */
function loadIsMinimized() {
  try {
    const minimized = localStorage.getItem(STORAGE_KEY_MINIMIZED);
    // Default to minimized if no preference stored
    return minimized === null ? true : minimized === "true";
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
    return true;
  }
}

/**
 * Save panel state to localStorage
 */
function saveIsMinimized(isMinimized) {
  try {
    localStorage.setItem(STORAGE_KEY_MINIMIZED, String(isMinimized));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Tracks whether a Bootstrap modal is currently open, via the `modal-open` class both
 * react-bootstrap and bootstrap.js toggle on <body>.
 *
 * The panel is a fixed bottom-right overlay (see panelStyle below), the same corner a header-edit
 * modal's footer buttons land in once it goes fullscreen on a narrow viewport (the shared EditModal
 * shell's `fullscreen="lg-down"`, used by every profile engine's header/employment/etc. modals) --
 * at that point the panel's z-index: 9999 sits on top of and hides the modal's Save/Close buttons.
 * Hiding the panel while any modal is open avoids that collision without touching modal z-index
 * globally.
 */
function useModalOpen() {
  const [modalOpen, setModalOpen] = useState(() => document.body.classList.contains("modal-open"));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setModalOpen(document.body.classList.contains("modal-open"));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return modalOpen;
}

/**
 * PanelContent - Renders the appropriate context panel
 *
 * Uses the plugin registry to find and render the right component
 * based on the detected context type.
 */
function PanelContent({ context }) {
  if (!context) {
    return <p className="small text-muted mb-0">No admin tools available for this page.</p>;
  }

  const { component: ContextComponent, props } = context;

  if (!ContextComponent) {
    return <p className="small text-muted mb-0">Unknown context type.</p>;
  }

  return <ContextComponent contextProps={props} />;
}

/**
 * ContextualPanel - Admin side panel for Frontend pages
 *
 * Provides quick admin actions based on the current page context.
 * Appears as a bottom-right overlay similar to Intercom chat widget.
 * Only visible to admin users.
 *
 * Architecture:
 * - The panel itself doesn't know about specific contexts
 * - Each context (User, Request, etc.) is a plugin that registers itself
 * - Plugins define URL patterns and components in contexts/*.jsx
 * - Adding a new context is as simple as creating a new file and importing it
 *
 * State Persistence:
 * - Panel minimized/open state is saved to localStorage
 * - State persists across page navigations
 * - Defaults to minimized on first visit
 */
function ContextualPanel() {
  const [context, setContext] = useState(null);
  const [isMinimized, setIsMinimized] = useState(loadIsMinimized);
  const modalOpen = useModalOpen();

  useEffect(() => {
    // Parse context from URL using the plugin registry
    const url = window.AdminPanelConfig?.currentUrl || window.location.pathname;
    const detectedContext = detectContext(url);
    setContext(detectedContext);
  }, []);

  // Wrapper function to update state and persist to localStorage
  const handleMinimize = (minimized) => {
    setIsMinimized(minimized);
    saveIsMinimized(minimized);
  };

  // Step aside for the page's own modals -- see useModalOpen for why.
  if (modalOpen) return null;

  // If no context detected, show a minimal indicator
  const hasContext = context !== null;

  // Using minimal inline styles - Bootstrap classes handle theming
  const panelStyle = {
    position: "fixed",
    bottom: "20px",
    // Sit left of the Intercom launcher (48px wide at right: 20px) so it doesn't cover us --
    // Intercom's z-index is ~2.1 billion, so we can't win by stacking above it.
    right: "88px",
    width: isMinimized ? "auto" : "280px",
    maxHeight: isMinimized ? "auto" : "400px",
    zIndex: 9999,
  };

  if (isMinimized) {
    return (
      <div id="admin-contextual-panel" style={panelStyle} className="rounded-3 shadow">
        <button
          type="button"
          className="btn btn-garish btn-sm d-flex align-items-center text-white"
          onClick={() => handleMinimize(false)}
          title="Expand Admin Panel"
        >
          <span className="me-2">🐐</span>
          Admin
        </button>
      </div>
    );
  }

  return (
    <div
      id="admin-contextual-panel"
      style={panelStyle}
      className="bg-body border border-garish rounded-3 shadow overflow-hidden"
    >
      <div className="panel-header d-flex justify-content-between align-items-center p-2 border-bottom border-garish bg-body-secondary">
        <div className="d-flex align-items-center">
          <span className="me-2">🐐</span>
          <strong className="small text-garish-emphasis">Admin</strong>
        </div>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => handleMinimize(true)} title="Minimize">
          <i className="fa fa-minus" />
        </button>
      </div>

      <div className="panel-body p-3">
        <PanelContent context={context} />

        {hasContext && <hr className="my-3" />}

        <div className="d-grid gap-2">
          <a href="/admin" className="btn btn-sm btn-outline-garish">
            <i className="fa fa-cog me-2" />
            Full Admin Panel
          </a>
        </div>
      </div>
    </div>
  );
}

export default ContextualPanel;
